import { ThemeOptions } from '@mui/material';
import mainThemeOptions from './mainOptions.theme';

const darkThemeOptions: ThemeOptions = {
  ...mainThemeOptions,
  palette: {
    mode: 'dark',
    primary: {
      main: 'hsla(0, 0%, 96%, 1)',
      dark: 'hsla(0, 0%, 93%, 1)',
      light: 'hsla(0, 0%, 100%, 1)',
      contrastText: 'hsla(0, 0%, 0%, 0.87)',
    },
    background: {
      paper: 'hsla(213, 11%, 16%, 1)',
      default: 'hsla(214, 25%, 11%, 1)',
    },
    action: {
      active: 'hsla(0, 0%, 80%, 0.56)',
      hover: 'hsla(0, 0%, 80%, 0.08)',
      selected: 'hsla(0, 0%, 80%, 0.16)',
      disabled: 'hsla(0, 0%, 80%, 0.3)',
      disabledBackground: 'hsla(0, 0%, 80%, 0.12)',
      focus: 'hsla(0, 0%, 80%, 0.12)',
    },
    info: {
      100: 'hsla(257, 64%, 88%, 1)',
      200: 'hsla(257, 72%, 82%, 1)',
      300: 'hsla(257, 76%, 76%, 1)',
      400: 'hsla(257, 78%, 71%, 1)',
      50: 'hsla(257, 58%, 94%, 1)',
      500: 'hsla(257, 80%, 64%, 1)',
      600: 'hsla(257, 81%, 61%, 1)',
      700: 'hsla(257, 59%, 54%, 1)',
      800: 'hsla(257, 51%, 42%, 1)',
      900: 'hsla(257, 52%, 30%, 1)',
      '160p': 'hsla(0, 0%, 80%, 1)',
      '190p': 'hsla(257, 81%, 61%, 0.12)',
      '30p': 'hsla(257, 81%, 70%, 0.3)',
      '4p': 'hsla(257, 81%, 61%, 0.04)',
      main: 'hsla(257, 100%, 74%, 1)',
      light: 'hsla(257, 76%, 76%, 1)',
      dark: 'hsla(257, 51%, 42%, 1)',
      contrastText: 'hsla(0, 0%, 100%, 1)',
    },
    error: {
      100: 'hsla(360, 78%, 30%, 1)',
      200: 'hsla(360, 78%, 35%, 1)',
      300: 'hsla(360, 78%, 40%, 1)',
      400: 'hsla(360, 78%, 45%, 1)',
      50: 'hsla(0, 78%, 20%, 1)',
      500: 'hsla(0, 78%, 50%, 1)',
      600: 'hsla(3, 75%, 55%, 1)',
      700: 'hsla(360, 78%, 65%, 1)',
      800: 'hsla(360, 78%, 75%, 1)',
      900: 'hsla(360, 78%, 80%, 1)',
      '160p': 'hsla(0, 78%, 50%, 1)',
      '190p': 'hsla(0, 78%, 60%, 0.2)',
      '30p': 'hsla(0, 78%, 55%, 0.3)',
      main: 'hsla(3, 75%, 55%, 1)',
      light: 'hsla(360, 78%, 40%, 1)',
      dark: 'hsla(360, 78%, 75%, 1)',
      contrastText: 'hsla(0, 0%, 100%, 1)',
    },
    warning: {
      100: 'hsla(29, 100%, 30%, 1)',
      200: 'hsla(29, 100%, 35%, 1)',
      300: 'hsla(29, 100%, 40%, 1)',
      400: 'hsla(28, 100%, 45%, 1)',
      50: 'hsla(29, 99%, 20%, 1)',
      500: 'hsla(28, 100%, 50%, 1)',
      600: 'hsla(27, 100%, 55%, 1)',
      700: 'hsla(26, 100%, 60%, 1)',
      800: 'hsla(24, 100%, 65%, 1)',
      900: 'hsla(22, 100%, 75%, 1)',
      '160p': 'hsla(27, 100%, 45%, 1)',
      '190p': 'hsla(27, 100%, 55%, 0.2)',
      main: 'hsla(27, 100%, 55%, 1)',
      light: 'hsla(29, 100%, 40%, 1)',
      dark: 'hsla(24, 100%, 65%, 1)', // White for contrast
    },
    success: {
      100: 'hsla(145, 87%, 30%, 1)',
      200: 'hsla(145, 82%, 35%, 1)',
      300: 'hsla(145, 74%, 40%, 1)',
      400: 'hsla(145, 100%, 45%, 1)',
      50: 'hsla(145, 93%, 20%, 1)',
      500: 'hsla(145, 100%, 50%, 1)',
      600: 'hsla(122, 33%, 60%, 1)',
      700: 'hsla(145, 100%, 65%, 1)',
      800: 'hsla(145, 100%, 75%, 1)',
      900: 'hsla(145, 100%, 80%, 1)',
      '160p': 'hsla(145, 100%, 50%, 1)',
      '190p': 'hsla(145, 100%, 60%, 0.2)',
      main: 'hsla(122, 33%, 60%, 1)',
      light: 'hsla(145, 74%, 40%, 1)',
      dark: 'hsla(145, 100%, 75%, 1)',
      contrastText: 'hsla(0, 0%, 100%, 1)',
    },
    grey: {
      100: 'hsla(0, 0%, 20%, 1)',
      200: 'hsla(0, 0%, 30%, 1)',
      300: 'hsla(0, 0%, 40%, 1)',
      400: 'hsla(0, 0%, 50%, 1)',
      50: 'hsla(0, 0%, 10%, 1)',
      500: 'hsla(0, 0%, 60%, 1)',
      600: 'hsla(0, 0%, 70%, 1)',
      700: 'hsla(0, 0%, 80%, 1)',
      800: 'hsla(0, 0%, 90%, 1)',
      900: 'hsla(0, 0%, 95%, 1)',
    },
    secondary: {
      main: 'hsla(0, 0%, 26%, 1)',
      dark: 'hsla(0, 0%, 100%, 0.3)',
      light: 'hsla(0, 0%, 38%, 1)',
      contrastText: 'hsla(0, 0%, 100%, 1)',
    },
    text: {
      primary: 'hsla(0, 0%, 100%, 1)',
      secondary: 'hsla(0, 0%, 100%, 0.7)',
      disabled: 'hsla(0, 0%, 100%, 0.5)',
    },
    divider: 'hsla(0, 0%, 50%, 0.12)',
    common: {
      black: 'hsla(0, 0%, 0%, 1)',
      white: 'hsla(0, 0%, 100%, 1)',
    },
    other: {
      divider: 'hsla(0, 0%, 50%, 0.12)',
      outlined_border: 'hsla(0, 0%, 50%, 0.23)',
      standard_input_line: 'hsla(0, 0%, 50%, 0.42)',
      backdrop_overlay: 'hsla(0, 0%, 0%, 0.3)',
      rating_active: 'hsla(42, 100%, 50%, 1)',
      snackbar: 'hsla(0, 0%, 20%, 1)',
    },
  },
};

export default darkThemeOptions;
